// Airborne / on-ground detector with airport-armed takeoff logic.
//
// Subscribes to useGpsStore and runs a small state machine that
// emits "airborne" when we're confident the aircraft is flying, and
// "ground" when it's confident we're parked or landed. Other modules
// subscribe to this for two purposes today:
//
//   - Track-log auto-start: kick off recording when the wheels leave.
//   - Traffic altitude filter: only narrow the ±Nft cone once we're
//     actually in the air; on the ground show every aircraft.
//
// State machine (ground / armed / airborne):
//
//                        ┌─────────────┐
//          start  ────▶  │   GROUND    │  (no GPS, or far from any airport)
//                        └──────┬──────┘
//                               │  near an airport
//                               ▼
//                        ┌─────────────┐
//                        │    ARMED    │  pre-takeoff / taxi
//                        └──────┬──────┘
//                               │  GS ≥ 50 kt  OR  alt rise ≥ 100 ft
//                               ▼
//                        ┌─────────────┐
//                        │  AIRBORNE   │  flying
//                        └──────┬──────┘
//                               │  GS < 8 kt for 60 s  AND  alt steady
//                               ▼
//                        ┌─────────────┐
//                        │   GROUND    │  (or ARMED if still on airport)
//                        └─────────────┘
//
// The 'armed' state is the trick that beats the car-on-highway false
// positive: you're only allowed to transition to AIRBORNE from ARMED,
// and ARMED requires being within ~5 nm of a known airport. A car
// can't fake that — it can hit highway speed, but the path through
// ARMED never opens. The one exception is trigger (c) below (GS ≥
// 80 kt) which we keep as a trapdoor for aircraft that pop into our
// world above 5 nm from any field (drop-ins, off-field takeoffs, etc.).
//
// Triggers ARMED → AIRBORNE:
//   (a) GS ≥ 50 kt sustained for ≥ 5 sec   (the takeoff roll)
//   (b) Altitude has risen ≥ 100 ft from the last steady-ground
//       reading (climb-out signal — handles helo/STOL/glider).
//   (c) GS ≥ 80 kt (instantaneous, ARMED-state bypass).
//
// Triggers AIRBORNE → GROUND/ARMED:
//   GS < 8 kt for ≥ 60 sec AND altitude variance < 30 ft in the
//   same window. We then decide ARMED vs GROUND on the airport check.

import { useGpsStore } from '../stores/gpsStore';
import { fetchBbox } from './mapBbox';
import { useUiStore } from '../stores/uiStore';

export type AirborneState = 'unknown' | 'ground' | 'armed' | 'airborne';

interface Listener {
  (state: AirborneState, prev: AirborneState): void;
}

interface DetectorState {
  state: AirborneState;
  // Trailing groundspeed samples (kt) within FAST_TAKEOFF_WINDOW_MS.
  speedSamples: Array<{ t: number; gs: number }>;
  // Last "we look parked / taxiing" GPS altitude (ft MSL) used as
  // the climb-baseline reference. Updates while not yet AIRBORNE;
  // frozen once AIRBORNE.
  baselineAltFt: number | null;
  // Trailing altitude samples (ft MSL) for the stillness check.
  altSamples: Array<{ t: number; altFt: number }>;
  // Tail end of the "GS below LANDING_GS for N sec" window.
  groundedSinceMs: number | null;
  // Timestamp when we started seeing a sustained VS >= VS_GATE_FPM.
  // Used to gate trigger (c) at high GS: only transition if a real
  // climb has been observed in the last VS_GATE_WINDOW_MS.
  vsClimbStartMs: number | null;
  // Timestamp since we've been airborne-but-suspiciously-flat (ground
  // vehicle demotion logic). Null when condition is not met.
  groundVehicleSinceMs: number | null;
}

// Tunables. Treat these as the contract the heuristic exposes; resist
// tuning them without explicit field-test data.
const FAST_TAKEOFF_GS = 50;            // kt sustained for window
const FAST_TAKEOFF_WINDOW_MS = 5_000;
const SLOW_TAKEOFF_ALT_RISE_FT = 200;  // ft above baseline (raised from 100; reduces GPS-jitter / hill false-positives)
const HARD_TAKEOFF_GS = 120;           // kt instantaneous — plane-only (raised from 80; highway tops out ~85—90 kt)
const LANDING_GS = 8;                  // kt below which we're 'stopped'
const LANDING_WINDOW_MS = 60_000;      // sustained for this long
const ALT_STILLNESS_FT = 30;           // ft within trailing average
// Airport proximity
const NEAR_AIRPORT_RADIUS_NM = 5;
const NEAR_AIRPORT_BBOX_DEG = 0.12;    // ~7 nm bbox at mid-latitudes
const AIRPORT_CACHE_MS = 90_000;
const AIRPORT_CACHE_MOVE_DEG = 0.04;   // re-fetch if we move > ~2.5 nm
// Vertical-speed gate for trigger (c): require a genuine climb signal.
const VS_GATE_FPM = 300;               // fpm min to count as 'climbing'
const VS_GATE_WINDOW_MS = 5_000;       // window to sustain the climb
// Ground-vehicle demotion: if 'airborne' but conditions look like a car.
const GROUND_DEMOTE_ALT_SPREAD_FT = 100; // max alt variance to suspect ground
const GROUND_DEMOTE_DURATION_MS  = 90_000; // must persist this long
const NEAR_RUNWAY_RADIUS_NM = 1.0;     // tighter than NEAR_AIRPORT_RADIUS_NM for demotion check
// Groundspeed ceiling for the demotion check. Above this we're clearly
// not a highway vehicle — a US interstate tops out at 80 mph (~70 kt)
// in the fastest jurisdictions. A Cessna 182 cruises at 130+ kt and
// every other GA / fire-aviation platform we care about cruises higher
// still. Was previously missing entirely — the demotion would fire
// during straight-and-level cruise at altitude because cruise looks
// EXACTLY like 'flat altitude + not near runway' to the prior heuristic.
// Boss report 2026-05-13: track stopped mid-flight at 8500' / 133 mph GS
// (≈ 116 kt). Symptom matched this exact bug.
const GROUND_DEMOTE_MAX_GS_KT = 90;

const det: DetectorState = {
  state: 'unknown',
  speedSamples: [],
  baselineAltFt: null,
  altSamples: [],
  groundedSinceMs: null,
  vsClimbStartMs: null,
  groundVehicleSinceMs: null,
};

// Cache of the most recent nearby-airport check. Refreshed lazily as
// the fix moves; the result decides GROUND vs ARMED.
interface NearbyAirportCache {
  lat: number;
  lon: number;
  fetchedAtMs: number;
  nearbyCount: number;
  inFlight: boolean;
}
let airportCache: NearbyAirportCache | null = null;

const listeners: Listener[] = [];
let unsub: (() => void) | null = null;

/** Start the detector. Safe to call multiple times. */
export function startAirborneDetector(): void {
  if (unsub) return;
  // Seed baseline + airport cache from the current fix if any.
  const cur = useGpsStore.getState().fix;
  if (cur) {
    if (typeof cur.altMeters === 'number') {
      det.baselineAltFt = cur.altMeters * 3.28084;
    }
    prewarmAirportCache(cur.lat, cur.lon);
  }
  unsub = useGpsStore.subscribe((state) => {
    const fix = state.fix;
    if (!fix) return;
    const now = fix.timestamp;
    const gsKt = (fix.speedMps ?? 0) * 1.94384;
    const altFt = fix.altMeters != null ? fix.altMeters * 3.28084 : null;

    // Update trailing speed window.
    det.speedSamples.push({ t: now, gs: gsKt });
    const speedCutoff = now - FAST_TAKEOFF_WINDOW_MS;
    while (det.speedSamples.length > 0 && det.speedSamples[0].t < speedCutoff)
      det.speedSamples.shift();

    // Update trailing altitude window (60 s for stillness).
    if (altFt != null) {
      det.altSamples.push({ t: now, altFt });
      const altCutoff = now - LANDING_WINDOW_MS;
      while (det.altSamples.length > 0 && det.altSamples[0].t < altCutoff)
        det.altSamples.shift();
    }

    // Track vertical speed (fpm) for trigger (c) VS gate.
    // Compute over the VS_GATE_WINDOW_MS trailing window of alt samples.
    const vsWindowCutoff = now - VS_GATE_WINDOW_MS;
    const vsWindowSamples = det.altSamples.filter((s) => s.t >= vsWindowCutoff);
    let currentVsFpm = 0;
    if (vsWindowSamples.length >= 2) {
      const oldest = vsWindowSamples[0];
      const newest = vsWindowSamples[vsWindowSamples.length - 1];
      const dtSec = (newest.t - oldest.t) / 1000;
      if (dtSec > 0.5) {
        currentVsFpm = ((newest.altFt - oldest.altFt) / dtSec) * 60;
      }
    }
    // Track when we first started climbing above the VS gate.
    if (currentVsFpm >= VS_GATE_FPM) {
      if (det.vsClimbStartMs == null) det.vsClimbStartMs = now;
    } else {
      det.vsClimbStartMs = null;
    }
    // Helper: true when VS gate has been sustained for VS_GATE_WINDOW_MS.
    const vsGateSatisfied =
      det.vsClimbStartMs != null &&
      now - det.vsClimbStartMs >= VS_GATE_WINDOW_MS;

    // Keep the airport cache warm whenever we move.
    prewarmAirportCache(fix.lat, fix.lon);

    const prev = det.state;
    let next = prev;

    if (prev === 'airborne') {
      // Airborne → ground/armed when GS has been < LANDING_GS for the
      // window AND altitude variance over the same window is small.
      if (gsKt < LANDING_GS) {
        if (det.groundedSinceMs == null) det.groundedSinceMs = now;
        const dur = now - det.groundedSinceMs;
        const altOk = altSpreadFt(det.altSamples) <= ALT_STILLNESS_FT;
        if (dur >= LANDING_WINDOW_MS && altOk) {
          // Reset baseline for the next leg.
          if (altFt != null) det.baselineAltFt = altFt;
          next = nearAirportSync(fix.lat, fix.lon) ? 'armed' : 'ground';
          det.groundVehicleSinceMs = null;
        }
      } else {
        det.groundedSinceMs = null;
      }

      // Ground-vehicle demotion: if we've been 'airborne' for a while
      // but the conditions match a highway car that slipped through
      // trigger (c), demote back to ground. ALL of the following must
      // hold for GROUND_DEMOTE_DURATION_MS:
      //   - Altitude variance is small (parked / highway-flat)
      //   - We're NOT near a runway
      //   - Groundspeed is below GROUND_DEMOTE_MAX_GS_KT (~90 kt). This
      //     is the critical fourth gate added 2026-05-13. Cruise flight
      //     at altitude looks identical to "flat altitude + not near
      //     runway" to the prior 3-gate heuristic, so straight-and-
      //     level cruise at 130+ kt would be incorrectly demoted mid-
      //     flight. Highway vehicles top out at ~70 kt (80 mph) so 90 kt
      //     gives a generous margin while still catching cars / boats.
      if (next === 'airborne') {
        const altSpread = altSpreadFt(det.altSamples);
        const notNearRunway = !nearAirportWithinNm(fix.lat, fix.lon, NEAR_RUNWAY_RADIUS_NM);
        const slowEnoughToBeAVehicle = gsKt < GROUND_DEMOTE_MAX_GS_KT;
        if (altSpread < GROUND_DEMOTE_ALT_SPREAD_FT && notNearRunway && slowEnoughToBeAVehicle) {
          if (det.groundVehicleSinceMs == null) det.groundVehicleSinceMs = now;
          if (now - det.groundVehicleSinceMs >= GROUND_DEMOTE_DURATION_MS) {
            // Demote: clearly on the ground (highway, parked, etc.).
            if (altFt != null) det.baselineAltFt = altFt;
            next = 'ground';
            det.groundVehicleSinceMs = null;
          }
        } else {
          // Any of the three gates failed — reset the timer. A brief
          // jiggle of groundspeed back above 90 kt during cruise should
          // not require the full 90s window to re-arm; the moment we
          // look like flight again, we trust we're flying.
          det.groundVehicleSinceMs = null;
        }
      }
    } else {
      // Not yet airborne. First settle GROUND vs ARMED.
      const armed = nearAirportSync(fix.lat, fix.lon);
      const baseState: AirborneState = armed ? 'armed' : 'ground';
      if (prev === 'unknown' || prev !== baseState) {
        next = baseState;
      }

      // Then check the airborne triggers.
      // Trigger (c): GS >= HARD_TAKEOFF_GS (120 kt) — plane-only bypass.
      // Requires the VS gate to fire: must see VS >= VS_GATE_FPM
      // sustained for VS_GATE_WINDOW_MS OR alt rise >= SLOW_TAKEOFF_ALT_RISE_FT.
      // This additional gate prevents a highway car doing ~90 kt from
      // triggering 'airborne' on a GPS noise spike.
      if (
        gsKt >= HARD_TAKEOFF_GS &&
        (vsGateSatisfied ||
         (altFt != null && det.baselineAltFt != null &&
          altFt - det.baselineAltFt >= SLOW_TAKEOFF_ALT_RISE_FT))
      ) {
        next = 'airborne';
      }
      // Trigger (a): sustained takeoff roll, ARMED-only.
      else if (
        baseState === 'armed' &&
        det.speedSamples.length >= 2 &&
        det.speedSamples[0].t <= now - FAST_TAKEOFF_WINDOW_MS + 1500 &&
        det.speedSamples.every((s) => s.gs >= FAST_TAKEOFF_GS)
      ) {
        next = 'airborne';
      }
      // Trigger (b): altitude rise off baseline. Works from either
      // ARMED or GROUND — covers off-field departures, hot-air
      // balloon launches, etc.
      else if (
        altFt != null && det.baselineAltFt != null &&
        altFt - det.baselineAltFt >= SLOW_TAKEOFF_ALT_RISE_FT &&
        gsKt >= 20
      ) {
        next = 'airborne';
      }

      // While still ground/armed, keep the baseline rolling so a
      // long pre-flight at increasing density altitude doesn't trip
      // the (b) trigger from sitting in the sun.
      if (next !== 'airborne' && gsKt < LANDING_GS && altFt != null) {
        det.baselineAltFt = altFt;
      }
    }

    if (next !== prev) {
      det.state = next;
      // Auto-engage follow-ownship on takeoff so the map tracks the
      // aircraft without the pilot needing to tap anything.
      if (next === 'airborne' && prev !== 'airborne') {
        useUiStore.getState().setFollowOwnship(true);
      }
      for (const fn of listeners.slice()) {
        try { fn(next, prev); } catch (e) {
          console.warn('[airborneDetector] listener', e);
        }
      }
    }
  });
}

/** Stop the detector. Most callers won't call this; the detector is
 *  process-lifetime by design. */
export function stopAirborneDetector(): void {
  if (unsub) { unsub(); unsub = null; }
  listeners.length = 0;
  det.state = 'unknown';
  det.speedSamples.length = 0;
  det.altSamples.length = 0;
  det.baselineAltFt = null;
  det.groundedSinceMs = null;
  det.vsClimbStartMs = null;
  det.groundVehicleSinceMs = null;
}

export function getAirborneState(): AirborneState {
  return det.state;
}

/** Convenience: true when we're in the air. */
export function isAirborne(): boolean {
  return det.state === 'airborne';
}

/** Subscribe to airborne / ground / armed transitions. Returns an
 *  unsubscribe fn. Fires immediately with the current state once on
 *  subscribe so callers don't need a separate bootstrap path. */
export function onAirborneChange(fn: Listener): () => void {
  listeners.push(fn);
  try { fn(det.state, det.state); } catch { /* ignore */ }
  return () => {
    const i = listeners.indexOf(fn);
    if (i >= 0) listeners.splice(i, 1);
  };
}

/** Returns true when the detector considers us to be on the ground or
 *  very likely on the ground based on current sample data.
 *  'ground' state is definitive; 'unknown' with enough alt samples that
 *  look flat is also treated as likely-ground for conservative alerting. */
export function isLikelyGround(): boolean {
  if (det.state === 'ground') return true;
  if (det.state === 'unknown') {
    // Not enough data to be confident; if we have a few samples and they
    // look flat, assume ground.
    if (det.altSamples.length >= 3 && altSpreadFt(det.altSamples) < GROUND_DEMOTE_ALT_SPREAD_FT) {
      return true;
    }
    // No samples at all — default to likely-ground (conservative).
    if (det.altSamples.length === 0) return true;
  }
  return false;
}

function altSpreadFt(samples: Array<{ altFt: number }>): number {
  if (samples.length < 2) return Infinity;
  let min = Infinity, max = -Infinity;
  for (const s of samples) {
    if (s.altFt < min) min = s.altFt;
    if (s.altFt > max) max = s.altFt;
  }
  return max - min;
}

/** Variant of nearAirportSync with a tighter radius (for demotion). */
function nearAirportWithinNm(lat: number, lon: number, radiusNm: number): boolean {
  if (!airportCache || airportCache.inFlight) return false;
  const ageMs = Date.now() - airportCache.fetchedAtMs;
  if (ageMs > AIRPORT_CACHE_MS * 2) return false;
  const dLat = Math.abs(lat - airportCache.lat);
  const dLon = Math.abs(lon - airportCache.lon);
  if (dLat > AIRPORT_CACHE_MOVE_DEG || dLon > AIRPORT_CACHE_MOVE_DEG) return false;
  // Re-scan the cached airport items if we need a tighter radius.
  // For efficiency, if the cache says 0 airports in 5 nm, there are
  // definitely 0 in 1 nm. Short-circuit that case.
  if (airportCache.nearbyCount === 0) return false;
  // If we need tighter than the 5 nm armed radius, the cache count is
  // a superset — we can't confirm tighter without the raw list. In practice
  // the 1 nm check is used only for demotion; if there are airports
  // within 5 nm we stay conservative (don't demote).
  return airportCache.nearbyCount > 0 && radiusNm >= NEAR_AIRPORT_RADIUS_NM;
}

/** Synchronous nearby-airport check using the cached bbox query.
 *  Returns false when the cache is stale or wasn't pre-warmed; the
 *  state machine treats that as "not near an airport" until the
 *  async refresh completes. */
function nearAirportSync(lat: number, lon: number): boolean {
  if (!airportCache) return false;
  if (airportCache.inFlight && airportCache.nearbyCount === 0) {
    // First-ever lookup is in flight; we don't know yet. Stay GROUND
    // until the answer arrives.
    return false;
  }
  const ageMs = Date.now() - airportCache.fetchedAtMs;
  if (ageMs > AIRPORT_CACHE_MS * 2) return false;
  // Cache is keyed loosely on position. If we've moved far enough
  // since the fetch, treat as stale.
  const dLat = Math.abs(lat - airportCache.lat);
  const dLon = Math.abs(lon - airportCache.lon);
  if (dLat > AIRPORT_CACHE_MOVE_DEG || dLon > AIRPORT_CACHE_MOVE_DEG) return false;
  return airportCache.nearbyCount > 0;
}

function prewarmAirportCache(lat: number, lon: number): void {
  const now = Date.now();
  if (airportCache && airportCache.inFlight) return;
  if (
    airportCache &&
    now - airportCache.fetchedAtMs < AIRPORT_CACHE_MS &&
    Math.abs(airportCache.lat - lat) < AIRPORT_CACHE_MOVE_DEG &&
    Math.abs(airportCache.lon - lon) < AIRPORT_CACHE_MOVE_DEG
  ) return;

  const cache: NearbyAirportCache = {
    lat, lon,
    fetchedAtMs: now,
    nearbyCount: 0,
    inFlight: true,
  };
  airportCache = cache;

  const half = NEAR_AIRPORT_BBOX_DEG / 2;
  fetchBbox({
    layer: 'airports',
    west: lon - half,
    east: lon + half,
    south: lat - half,
    north: lat + half,
    zoom: 10,
    limit: 25,
  })
    .then((items) => {
      let count = 0;
      // Convert bbox hits to actual NM-radius hits. cos(lat) for the
      // longitude scale; ~60 nm per degree latitude.
      const cosLat = Math.cos((lat * Math.PI) / 180);
      for (const a of items) {
        const dLat = a.lat - lat;
        const dLon = (a.lon - lon) * cosLat;
        const distNm = Math.sqrt(dLat * dLat + dLon * dLon) * 60;
        if (distNm <= NEAR_AIRPORT_RADIUS_NM) count++;
      }
      if (airportCache === cache) {
        cache.nearbyCount = count;
        cache.inFlight = false;
      }
    })
    .catch(() => {
      if (airportCache === cache) {
        cache.inFlight = false;
        // On error, leave nearbyCount at 0 — we'd rather under-arm
        // than over-arm. Trigger (c) at 80 kt still catches a real
        // takeoff offline.
      }
    });
}
